import React, { useState, useEffect } from "react";
import { Box, Container, Typography, useMediaQuery } from "@mui/material";
import TermsAndConditions from "../../../features/terms-and-condition/TermsAndConditions";
import OptOutReason from "../../../features/opt-out-reaon/OptOutReason";
import { getPatientRoute } from "../../../utility/functions/common-functions";
import BannerContent from "../../../features/banner-content/BannerContent";
import { useGetOnboardingUIMessagesQuery } from "../services/GetOnboardingUIMessageAPISlice";
import { useGetReasonMessagesQuery } from "../services/GetReasonMessageAPISlice";
import CustomSpinner from "../../../components/custom-spinner/CustomSpinner";
import { useNavigate } from "react-router-dom";
import { useUpdatePatientProfileMutation } from "../services/PatientConsentAPISlice";
import {
  MESSAGE_IDS,
  PATIENTCASE_STATUS_ID_MAPPING,
  OPT_OUT_REASON_MSG_TYPE,
} from "../../../utility/constants/common-constants";
import { useAppSelector, useAppDispatch } from "../../../store/hooks";
import styles from "../styles/OptInOptOut.module.scss";
import BackgroundLayout from "../../../components/custom-backgrounds/BackgroundLayout";
import { useTranslation } from "react-i18next";
import optinOut from "../../../../src/assets/icons/optinOut.svg";
import CustomGroupedRadioButton from "../../../components/custom-grouped-radio-btns/CustomGroupedRadioButton";
import { ActiveLanguages } from "../../../utility/interfaces/masterDataInterfaces";
import { enqueueSnackbar } from "notistack";
import { usePreferredLangMutation } from "../../../features/header-component/SavePreferredLangAPISlice";
import CustomLanguage from "../../../components/v2/custom-language-selector/CustomLanguageSelector";
import { patientMasterDataActions } from "../../../features/header-component/services/PatientMasterDataSlice";
import { useGetPatientActiveLanguageQuery } from "../../../features/header-component/GetPatientActiveLanguageSlice";
import { Reasons } from "../../../utility/interfaces/carePlanInterfaces";
import { LockIcon } from "../../../assets/icons/LockSvg";
import { setHideNoShow } from "../services/PatientStatusSlice";

const OptInOptOut: React.FC = () => {
  const height = {
    xs: "15.38rem",
    sm: "16.88rem",
    md: "16.31rem",
  };

  const [selectedOption, setSelectedOption] = useState<
    "optIn" | "optOut" | null
  >(null);
  const [showOptOut, setShowOptOut] = useState(false);

  const [optOutReason, setOptOutReason] = useState<number | null>(null);
  const [updatePatientProfile] = useUpdatePatientProfileMutation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const radioOptions = [
    { label: t("optInOutRadio.optIn"), value: true },
    { label: t("optInOutRadio.optOut"), value: false },
  ];
  const patientPreferredLang = useAppSelector(
    (state) => state.patientOnboardingStatus.preferredLanguage
  );
  const { data, error, isLoading } =
    useGetOnboardingUIMessagesQuery(patientPreferredLang); // Fetch the onboarding messages

    const { data: reasonList } = useGetReasonMessagesQuery(patientPreferredLang, {
      skip: !(patientPreferredLang && showOptOut),
    });

  const { patientProfileId, patientCaseStatusId, patientCaseId } =
    useAppSelector((state) => state.patientOnboardingStatus);

  const [radioValue, setRadioValue] = useState(true);

  const userName = useAppSelector(
    (state) => state.patientOnboardingStatus.userName || "USER"
  );

  const [selectedLanguage, setSelectedLanguage] =
    useState(patientPreferredLang);

  const handleInputChange = (value: any) => {
    setRadioValue(value);
    if (value) {
      setSelectedOption("optIn");
      setShowOptOut(false);
    } else {
      setSelectedOption("optOut");
      setShowOptOut(true);
    }
  };
  const handleOptOutProceed = async () => {
    if (selectedOption === "optOut" && optOutReason !== null) {
      try {
        await updatePatientProfile({
          body: {
            patientProfileId,
            optInSelection: false,
            termsAgreement: false,
            updatedBy: "system",
            patientCaseId,
            optOutReasonId: optOutReason,
          },
        }).unwrap();
      } catch (error) {
        enqueueSnackbar(t("anErrorOccurred"), { variant: "error" });
      }
    }
  };

  const getMessageByCode = (code: string) =>
    data?.messageContext?.find((message) => message?.messageCode === code)
      ?.content || "";

  const [languageAnchorEl, setLanguageAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const isLanguageMenuOpen = Boolean(languageAnchorEl);

  const handleLanguageClick = (event: React.MouseEvent<HTMLElement>) => {
    setLanguageAnchorEl(event.currentTarget);
  };

  const [PreferredLangSave] = usePreferredLangMutation();

  const onChangeLang = async (selectedLang: ActiveLanguages) => {
    if (
      selectedLang.languageName.toLowerCase() !== selectedLanguage.toLowerCase()
    ) {
      try {
        i18n.changeLanguage(selectedLang.languageCode);
        setSelectedLanguage(selectedLang.languageName); // Update the selected language
        setLanguageAnchorEl(null);

        const body = {
          patientProfileId: patientProfileId,
          prefferedLanguageId: selectedLang.languageId,
          userName: userName,
        };

        await PreferredLangSave({ body }).unwrap();
      } catch (err: any) {
        enqueueSnackbar(t("anErrorOccurred"), { variant: "error" });
      } finally {
        window.location.reload();
      }
    }
  };

  const { data: languageList } = useGetPatientActiveLanguageQuery({});

  useEffect(() => {
    if (languageList) {
      dispatch(patientMasterDataActions.setActiveLanguages(languageList), []);
    }

    i18n.changeLanguage(patientPreferredLang);

    const lang = languageList?.supportedLanguages.filter(
      (row: ActiveLanguages) => {
        return row.languageCode === patientPreferredLang;
      }
    );

    lang && setSelectedLanguage(lang[0]?.languageName);
  }, [languageList, patientPreferredLang]);

  const welcomeMessage = getMessageByCode(MESSAGE_IDS.WELCOME);
  const welcomeMessageHeading = getMessageByCode(MESSAGE_IDS.WELCOME_HEADING);
  const termsAndConditionsContent = getMessageByCode(
    MESSAGE_IDS.TERMS_AND_CONDITIONS
  );
  const optOutMessage = getMessageByCode(MESSAGE_IDS.OPT_OUT);
  const optOutErrorMessage = getMessageByCode(MESSAGE_IDS.OPT_OUT_ERROR);
  const journeyCompletedMessage = getMessageByCode(
    MESSAGE_IDS.JOURNEY_COMPLETED
  );
  const authorizationErrorMessage = getMessageByCode(
    MESSAGE_IDS.AUTHORIZATION_ERROR
  );

  const getOptOutMessages = (messages: Reasons[]) => {
    return messages.filter(
      (message) => message.categoryCode === OPT_OUT_REASON_MSG_TYPE
    );
  };

  const optOutReasons = getOptOutMessages(
    reasonList?.categoryReasons || []
  ).map((message) => ({
    label: message.displayReason,
    value: message.reasonId.toString(),
  }));

  const handleOptInProceed = async () => {
    // Store the message ID for successful opt-in
    localStorage.setItem("showBannerOverlay", "true");

    // Make the API call to update the patient profile
    try {
      await updatePatientProfile({
        body: {
          patientProfileId,
          optInSelection: true,
          termsAgreement: true,
          updatedBy: "system",
          patientCaseId,
          optOutReasonId: 0,
        },
      }).unwrap();
      dispatch(setHideNoShow(true));
      navigate(getPatientRoute(patientProfileId, ""));
    } catch (error) {
      enqueueSnackbar(t("anErrorOccurred"), { variant: "error" });
    }
  };

  if (isLoading) return <CustomSpinner />;
  if (error && "status" in error) {
    if (error.status >= 400 && error.status <= 500) {
      navigate("/", { replace: true });
    }
  }

  if (
    patientCaseStatusId ===
    (PATIENTCASE_STATUS_ID_MAPPING.inactive ||
      PATIENTCASE_STATUS_ID_MAPPING.optout)
  ) {
    return (
      <BannerContent
        html={true}
        title={t("oops!")}
        message={optOutErrorMessage}
        icon={
          <LockIcon
            width="95"
            height="115"
          />
        }
      />
    );
  }

  if (patientCaseStatusId === PATIENTCASE_STATUS_ID_MAPPING.journeyCompleted) {
    return (
      <BannerContent
        html={true}
        title={t("oops!")}
        message={journeyCompletedMessage}
        icon={
          <LockIcon
            width="95"
            height="115"
          />
        }
      />
    );
  }

  if (patientCaseStatusId === PATIENTCASE_STATUS_ID_MAPPING.enrollmentPending) {
    return (
      <BackgroundLayout height={height}>
        <Container
          maxWidth={false}
          style={{
            minHeight: "100vh",
            padding: "0px",
          }}
        >
          <Box className={styles.userAcceptanceMainContianer}>
            <Box className={styles.userAcceptanceContainer}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  padding: {
                    xs: "30px 0px 20px 0px",
                    sm: "50px 0px 30px 0px",
                  },
                }}
              >
                <img
                  src={optinOut}
                  alt="OptinOut Logo"
                />
              </Box>
              <Typography
                variant="h3"
                marginBottom={4}
                sx={{ textAlign: "center" }}
              >
                {welcomeMessageHeading}
              </Typography>

              <Typography
                variant="body1"
                mb={4}
                sx={{ textAlign: "center" }}
              >
                {welcomeMessage}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                mt: 2,
                gap: 4,
              }}
            ></Box>
          </Box>

          <Box sx={{ textAlign: "center", padding: "50px 0px 30px 0px" }}>
            {t("readyToTakeFirstStep")}
          </Box>
          <Box className={styles.termConditionsContainer}>
            <Box
              sx={{
                textAlign: "center",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Box>
                <CustomGroupedRadioButton
                  disabled={false}
                  options={radioOptions}
                  changeTrigger={(value) => handleInputChange(value)}
                  value={radioValue}
                />
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Box>
                {showOptOut && (
                  <OptOutReason
                    message={optOutMessage}
                    reasons={optOutReasons}
                    handleOptOutProceed={handleOptOutProceed}
                    setOptOutReason={setOptOutReason}
                  />
                )}
              </Box>
            </Box>
            {!showOptOut && (
              <TermsAndConditions
                content={termsAndConditionsContent}
                handleOptInProceed={handleOptInProceed}
              />
            )}

            <Box
              sx={{
                display: "flex",
                flexDirection: "space-evenly",
                justifyContent: "center",
              }}
            >
              <Box>
                {languageList?.supportedLanguages && (
                  <CustomLanguage
                    onLangMenuOpen={handleLanguageClick}
                    selectedLanguage={selectedLanguage}
                    anchorEl={languageAnchorEl}
                    open={isLanguageMenuOpen}
                    onClose={() => setLanguageAnchorEl(null)}
                    supportedLanguages={languageList?.supportedLanguages}
                    onChangeLang={onChangeLang}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Container>
      </BackgroundLayout>
    );
  }

  return <CustomSpinner />;
};

export default OptInOptOut;
